'use client'

import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { useAuth } from '@/context/auth-context'

interface ProviderContextValue {
  /** De geselecteerde aanbieder voor impersonation, of null */
  selectedProvider: ProviderInfo | null
  /** Activeer provider-impersonation */
  setSelectedProvider: (provider: ProviderInfo | null) => void
  /** Of de provider-modus actief is */
  isProviderMode: boolean
  /** Verlaat provider-modus */
  exitProviderMode: () => void
}

export interface ProviderInfo {
  uuid: string
  name: string
  cocNumber?: string
  agbCode?: string
  city?: string
}

const ProviderContextCtx = createContext<ProviderContextValue>({
  selectedProvider: null,
  setSelectedProvider: () => {},
  isProviderMode: false,
  exitProviderMode: () => {},
})

/**
 * Provider Context — beheert de "Bekijk als aanbieder" modus.
 *
 * Alleen beschikbaar voor gebruikers met ROLE_ADMIN of ROLE_SUPER_ADMIN.
 * Slaat de geselecteerde provider op in localStorage zodat deze behouden
 * blijft bij page refreshes.
 */
export function ProviderContextProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  const [selectedProvider, setSelectedProviderState] = useState<ProviderInfo | null>(null)

  const isAdmin = user?.roles?.includes('ROLE_ADMIN') || user?.roles?.includes('ROLE_SUPER_ADMIN')

  // Herstel provider-context uit localStorage bij mount
  useEffect(() => {
    if (typeof window === 'undefined' || !isAdmin) return

    const stored = localStorage.getItem('providerContext')
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as ProviderInfo
        if (parsed?.uuid) {
          setSelectedProviderState(parsed)
        }
      } catch {
        localStorage.removeItem('providerContext')
      }
    }
  }, [isAdmin])

  const setSelectedProvider = useCallback(
    (provider: ProviderInfo | null) => {
      if (!isAdmin) return

      setSelectedProviderState(provider)
      if (provider) {
        localStorage.setItem('providerContext', JSON.stringify(provider))
      } else {
        localStorage.removeItem('providerContext')
      }
    },
    [isAdmin]
  )

  const exitProviderMode = useCallback(() => {
    setSelectedProviderState(null)
    localStorage.removeItem('providerContext')
  }, [])

  const isProviderMode = selectedProvider !== null && isAdmin === true

  return (
    <ProviderContextCtx.Provider
      value={{
        selectedProvider,
        setSelectedProvider,
        isProviderMode,
        exitProviderMode,
      }}
    >
      {children}
    </ProviderContextCtx.Provider>
  )
}

export function useProviderContext() {
  return useContext(ProviderContextCtx)
}
